import React, { useEffect, useState } from 'react';
import { Field, useFormikContext } from 'formik';
import {
    FormControl,
    FormHelperText,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
    Typography,
    SelectChangeEvent,
} from '@mui/material';
import { Info } from '@mui/icons-material';
import { ComboBox, LegalEntityIdField, VATField } from '@fnol/library/src/components';
import { constValues, personType, question50MaxLength, translations, questionMaxLength } from '../../lib/consts';
import { isTemplateExpression } from 'typescript';
import { GenericObject } from '@fnol/library/src/lib';

type Props = {
    legalEntityType: personType;
};

const LegalEntity = ({ legalEntityType }: Props) => {
    const { values, errors, touched, handleChange, setFieldValue } = useFormikContext<any>();

    const [appropriateRoles, setAppropriateRoles] = useState([]);

    const [selectedRoles, setselectedRoles] = useState();

    const role = `${personType[legalEntityType]}LegalEntityPersonRole`;
    const name = `${personType[legalEntityType]}LegalEntityName`;
    const id = `${personType[legalEntityType]}LegalEntityID`;
    const vat = `${personType[legalEntityType]}LegalEntityVAT`;
    const country = `${personType[legalEntityType]}LegalEntityCountry`;
    const city = `${personType[legalEntityType]}LegalEntityCity`;
    const cityInSerbia = `${personType[legalEntityType]}LegalEntityCityInSerbia`;
    const street = `${personType[legalEntityType]}LegalEntityStreet`;
    const streetNumber = `${personType[legalEntityType]}LegalEntityStreetNumber`;
    const email = `${personType[legalEntityType]}LegalEntityEmail`;

    const handleChangeRole = (event: SelectChangeEvent<HTMLSelectElement>) => {
        const selectedRole = values.roles.find((item: GenericObject) => item.id === event.target.value);
        setFieldValue(role, selectedRole);
        setselectedRoles(selectedRole['id']);
    };

    useEffect(() => {
        const legalRoles = values.roles.filter((item: GenericObject) => item.legalEntity);
        setAppropriateRoles(legalRoles);
        if (values[role] && values[role].id) {
            setselectedRoles(values[role].id);
        }
    }, []);

    return (
        <>
            <FormControl>
                <InputLabel id={`${role}-label`}>{translations.LEGAL_ENTITY_PERSON_ROLE}</InputLabel>

                <Select
                    id={role}
                    labelId={`${role}-label`}
                    name={role}
                    value={selectedRoles || ''}
                    label={translations.LEGAL_ENTITY_PERSON_ROLE}
                    error={!!(touched[role] && errors[role])}
                    onChange={handleChangeRole}
                >
                    {appropriateRoles.map((option: any) => {
                        return (
                            <MenuItem key={option.id} value={option.id}>
                                {option.value}
                            </MenuItem>
                        );
                    })}
                </Select>

                {touched[role] && errors[role] ? (
                    <FormHelperText error={true}>
                        <>{errors[role]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={name}
                    label={translations.legalInfo.SUBMITTER_LEGAL_NAME}
                    as={TextField}
                    inputProps={{
                        maxLength: question50MaxLength,
                    }}
                    error={!!(touched[name] && errors[name])}
                    required
                />
                {touched[name] && errors[name] ? (
                    <FormHelperText error={true}>
                        <>{errors[name]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            {values[country] !== constValues.countrySerbiaId ? (
                <FormControl>
                    <Field
                        name={city}
                        label={translations.CITY}
                        as={TextField}
                        inputProps={{
                            maxLength: question50MaxLength,
                        }}
                        error={!!(touched[city] && errors[city])}
                    />
                    {touched[city] && errors[city] ? (
                        <FormHelperText error={true}>
                            <>{errors[city]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            ) : (
                <FormControl error={!!(touched[cityInSerbia] && errors[cityInSerbia])}>
                    <Field
                        name={cityInSerbia}
                        label={translations.CITY}
                        options={
                            values.cities || [
                                {
                                    id: '',
                                    value: '',
                                    text: '',
                                    code: '',
                                },
                            ]
                        }
                        onChange={handleChange}
                        defaultValue={values[cityInSerbia]}
                        as={ComboBox}
                        error={!!(touched[cityInSerbia] && errors[cityInSerbia])}
                    />
                    {touched[cityInSerbia] && errors[cityInSerbia] ? (
                        <FormHelperText error={true}>
                            <>{errors[cityInSerbia]}</>
                        </FormHelperText>
                    ) : null}
                </FormControl>
            )}

            <FormControl>
                <Field
                    name={street}
                    label={translations.STREET}
                    as={TextField}
                    inputProps={{
                        maxLength: question50MaxLength,
                    }}
                    error={!!(touched[street] && errors[street])}
                />
                {touched[street] && errors[street] ? (
                    <FormHelperText error={true}>
                        <>{errors[street]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={streetNumber}
                    label={translations.STREET_NUMBER}
                    as={TextField}
                    inputProps={{
                        maxLength: question50MaxLength,
                    }}
                    error={!!(touched[streetNumber] && errors[streetNumber])}
                />
                {touched[streetNumber] && errors[streetNumber] ? (
                    <FormHelperText error={true}>
                        <>{errors[streetNumber]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={id}
                    label={translations.legalInfo.ID}
                    as={LegalEntityIdField}
                    selectedCountry={values[country]}
                    error={!!(touched[id] && errors[id])}
                    required
                />
                {touched[id] && errors[id] ? (
                    <FormHelperText error={true}>
                        <>{errors[id]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={vat}
                    label={translations.legalInfo.VAT}
                    as={VATField}
                    selectedCountry={values[country]}
                    error={!!(touched[vat] && errors[vat])}
                    required
                />
                {touched[vat] && errors[vat] ? (
                    <FormHelperText error={true}>
                        <>{errors[vat]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>

            <FormControl>
                <Field
                    name={email}
                    label={translations.legalInfo.EMAIL}
                    type="email"
                    as={TextField}
                    inputProps={{
                        maxLength: questionMaxLength,
                    }}
                    error={!!(touched[email] && errors[email])}
                />
                {touched[email] && errors[email] ? (
                    <FormHelperText error={true}>
                        <>{errors[email]}</>
                    </FormHelperText>
                ) : null}
            </FormControl>
        </>
    );
};

export const LegalEntityCleanup = (
    type: personType,
    setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void,
) => {
    setFieldValue(`${personType[type]}LegalEntityPersonRole`, null);
    setFieldValue(`${personType[type]}LegalEntityName`, '');
    setFieldValue(`${personType[type]}LegalEntityID`, '');
    setFieldValue(`${personType[type]}LegalEntityVAT`, '');
    setFieldValue(`${personType[type]}LegalEntityCountry`, constValues.countrySerbiaId);
    setFieldValue(`${personType[type]}LegalEntityCity`, '');
    setFieldValue(`${personType[type]}LegalEntityCityInSerbia`, '');
    setFieldValue(`${personType[type]}LegalEntityStreet`, '');
    setFieldValue(`${personType[type]}LegalEntityStreetNumber`, '');
    setFieldValue(`${personType[type]}LegalEntityEmail`, '');
};

export default LegalEntity;
